/**
 * L6.SEAL.1 — Deterministic ASCII/ANSI archive cover seal.
 *
 * The backend signs a `visualSeed` (32 hex chars) into every sealed run
 * result (L3.SEED.1). The Archive renders that seed as a deterministic
 * ASCII seal: same seed -> same seal, no external assets.
 *
 * Pure module: no DOM, no fetch, no network — runs in the render thread
 * and in Node/vitest.
 */

// Block character ramp from empty to fully lit (classic ANSI density ramp).
export const SEAL_RAMP = [' ', '.', ':', ';', '*', '@'];

// Monospace grid. 80x24 fits 640x400 at 8px/col x 16px/row (exact 1:1 with
// the 8x16 DOS cell); portrait 360x640 renders the same grid shrunk.
export const SEAL_WIDTH = 80;
export const SEAL_HEIGHT = 24;

const RAMP_LEN = SEAL_RAMP.length;

/**
 * Normalize the backend visualSeed into a 32-char lowercase hex string.
 * Tolerates legacy/missing values deterministically (empty -> 32 zeros) so
 * the seal never renders `undefined`/`NaN`.
 */
export function normalizeSeed(seed) {
  const value = String(seed ?? '').toLowerCase().replace(/[^0-9a-f]/g, '');
  if (value.length >= 32) return value.slice(0, 32);
  return value.padEnd(32, '0');
}

function nibbleToBand(seed, i) {
  const nibble = parseInt(seed[i], 16);
  // Map nibble 0..15 onto the 0..4 ramp band (inclusive) deterministically.
  return Math.min(RAMP_LEN - 1, Math.floor((nibble * RAMP_LEN) / 16));
}

/**
 * Build the seal grid: 32 hex chars -> 32 lit columns, mirrored to fill
 * `width` (odd widths get the central column duplicated). Row bands come
 * from the same nibble, so the seal is a stable, seed-specific silhouette.
 */
export function sealGrid(seed, width = SEAL_WIDTH, height = SEAL_HEIGHT) {
  const normalized = normalizeSeed(seed);
  const grid = [];
  for (let row = 0; row < height; row += 1) {
    const chars = [];
    for (let col = 0; col < width; col += 1) {
      const nibbleIndex = Math.min(31, Math.floor((col * 32) / width));
      const band = nibbleToBand(normalized, nibbleIndex);
      // Edge rows stay dark so the seal reads as a stamped block, not a flood.
      const lit = row > 0 && row < height - 1 && band > 0 ? band : 0;
      chars.push(SEAL_RAMP[lit]);
    }
    grid.push(chars.join(''));
  }
  return grid;
}

/** Stable hash over the grid rows — the snapshot value tests assert on. */
export function sealFingerprint(grid) {
  let hash = 0;
  for (const row of grid) {
    for (let i = 0; i < row.length; i += 1) {
      hash = (hash * 31 + row.charCodeAt(i)) >>> 0;
    }
    hash = (hash + 0x9e3779b9) >>> 0;
  }
  return hash.toString(16).padStart(8, '0');
}

/**
 * Render the seal onto a 2D canvas context using the in-memory 8x16 DOS
 * bitmap font (no font files, no assets). Deterministic for a given ctx.
 *
 * @param {CanvasRenderingContext2D} ctx
 * @param {string} seed
 * @param {number} x top-left x
 * @param {number} y top-left y
 * @param {number} width available width (px)
 * @param {number} height available height (px)
 * @param {string} [color] fill color (default #39FF14)
 * @returns {{rows: number, cols: number, cell: number, rowsUsed: number}}
 */
export function renderSeal(
  ctx,
  seed,
  x,
  y,
  width,
  height,
  color = '#39FF14',
) {
  const grid = sealGrid(seed);
  const cols = grid[0].length;
  const rows = grid.length;
  const cell = Math.max(1, Math.floor(Math.min(width / cols, height / rows)));
  const originX = x + Math.floor((width - cols * cell) / 2);
  const originY = y + Math.floor((height - rows * cell) / 2);

  const prevFill = ctx.fillStyle;
  const prevAlpha = ctx.globalAlpha;
  ctx.fillStyle = color;
  ctx.globalAlpha = 1;
  for (let row = 0; row < rows; row += 1) {
    const line = grid[row];
    for (let col = 0; col < cols; col += 1) {
      if (line[col] === ' ') continue;
      ctx.fillRect(originX + col * cell, originY + row * cell, cell, cell);
    }
  }
  ctx.fillStyle = prevFill;
  ctx.globalAlpha = prevAlpha;
  return { rows, cols, cell, rowsUsed: rows };
}

/**
 * Plain-ANSI variant for terminals/debug (no canvas). Returns a string with
 * ANSI 38;2 green shading per density band; empty bands emit no escape.
 */
export function sealAnsi(seed) {
  const grid = sealGrid(seed);
  const green = (intensity) =>
    intensity === 0 ? '' : `\x1b[38;2;${Math.round(57 * intensity / (RAMP_LEN - 1))};${Math.round(255 * (0.4 + 0.6 * intensity / (RAMP_LEN - 1)))};${Math.round(20 * intensity / (RAMP_LEN - 1))}m`;
  return grid
    .map((line) => {
      let out = '';
      for (const ch of line) {
        const band = SEAL_RAMP.indexOf(ch);
        out += green(band) + ch;
      }
      return out + '\x1b[0m';
    })
    .join('\n');
}
